import { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { deleteProductionOrders, listProductionOrders } from "../api/productionOrders";
import { EmptyState, PageHeader, StatusBadge } from "../components/ui";
import WorkOrderPaperReservationDialog from "../components/WorkOrderPaperReservationDialog";
import WorkOrderKnifeDialog from "../components/WorkOrderKnifeDialog";
import WorkOrderMaterialsDialog from "../components/WorkOrderMaterialsDialog";
import WorkOrderShipmentDialog from "../components/WorkOrderShipmentDialog";

const ITEMS_PER_PAGE = 20;

function formatDateOnly(val) {
  if (!val) return "";
  const s = String(val).trim();
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}

// Siparişler ekranındaki "Miktar (Paket)" ile aynı biçim.
function formatInteger(value) {
  const number = Number(value);
  return Number.isFinite(number) ? String(Math.trunc(number)) : "—";
}

function orderNoCell(order) {
  const details = Array.isArray(order?.variant_details) ? order.variant_details : [];
  const productName = order?.product_material_name || "";
  // component="span": Tooltip çocuğu <span> ile sarmalanıyor, <p> geçersiz iç içe geçme yapar.
  const number = (
    <Typography component="span" sx={{ fontWeight: 900, color: "#312e81" }}>
      {order?.order_no || "—"}
    </Typography>
  );
  if (!details.length && !productName) return number;

  return (
    <Tooltip
      arrow
      placement="right"
      title={
        <div>
          {productName ? (
            <div style={{ fontWeight: 700, marginBottom: 6 }}>{productName}</div>
          ) : null}
          {details.map((detail, index) => (
            <div key={`${detail?.label || "dim"}-${index}`} style={{ whiteSpace: "nowrap" }}>
              <span style={{ opacity: 0.75 }}>{detail?.label}:</span>{" "}
              <b>{detail?.value}{detail?.unit ? ` ${detail.unit}` : ""}</b>
            </div>
          ))}
        </div>
      }
    >
      <span style={{ cursor: "help", textDecoration: "underline dotted" }}>{number}</span>
    </Tooltip>
  );
}

function currentUserRole() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null")?.role || null;
  } catch {
    return null;
  }
}

export default function WorkOrders() {
  // Rezervasyon ve bıçak atama üretim müdürü rolüne özel; admin de yapabilir.
  const role = currentUserRole();
  const canAssign = role === "uretim_muduru" || role === "admin";
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [reservationWorkOrder, setReservationWorkOrder] = useState(null);
  const [knifeWorkOrder, setKnifeWorkOrder] = useState(null);
  const [materialsWorkOrder, setMaterialsWorkOrder] = useState(null);
  const [shipmentWorkOrder, setShipmentWorkOrder] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  // Yazarken her tuşta istek atmamak için aramayı geciktir.
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  // Filtre değişince ilk sayfaya dön; aksi halde boş sayfada kalınabilir.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, status]);

  useEffect(() => {
    let cancelled = false;
    function load() {
      listProductionOrders({
        page: page + 1,
        limit: ITEMS_PER_PAGE,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(status ? { status } : {}),
      })
        .then((data) => {
          if (cancelled) return;
          setList(data.items || []);
          setTotal(data.total || 0);
        })
        .catch((err) => {
          if (cancelled) return;
          console.error("WORK ORDERS LOAD ERROR", err);
          alert("İş emirleri yüklenemedi");
        });
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [page, debouncedSearch, status]);

  function toggleSelected(id) {
    const numericId = Number(id);
    setSelectedIds((current) =>
      current.includes(numericId)
        ? current.filter((itemId) => itemId !== numericId)
        : [...current, numericId]
    );
  }

  async function handleDeleteSelected() {
    if (!selectedIds.length) return;
    if (!window.confirm("Seçili iş emirlerini silmek istediğinize emin misiniz?")) return;

    setDeleting(true);
    try {
      await deleteProductionOrders(selectedIds);
      const data = await listProductionOrders({ page: 1, limit: ITEMS_PER_PAGE });
      setList(data.items || []);
      setTotal(data.total || 0);
      setSelectedIds([]);
      setPage(0);
    } catch (err) {
      console.error("WORK ORDERS DELETE ERROR", err);
      alert(err?.response?.data?.error?.message || "İş emirleri silinemedi.");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Stack spacing={2.5} sx={{ maxWidth: 1540, mx: "auto" }}>
      <PageHeader
        title="İş Emirleri"
        subtitle="Üretim iş emirlerini listeleyin."
        actions={<Paper elevation={0} sx={{ px: 1.5, py: 1, borderRadius: 3, bgcolor: "#f5f3ff", border: "1px solid #c7d2fe" }}><Typography variant="body2" sx={{ color: "#3730a3", fontWeight: 800 }}>Toplam {total} iş emri</Typography></Paper>}
      />

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} justifyContent="flex-end" alignItems={{ xs: "stretch", sm: "center" }}>
        <TextField
          size="small"
          placeholder="Müşteri kodu / adı, sipariş no, iş emri no, ürün kodu"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: { sm: 340 }, bgcolor: "#fff", borderRadius: 2 }}
          InputProps={{
            endAdornment: search ? (
              <IconButton size="small" onClick={() => setSearch("")} aria-label="Aramayı temizle">
                <span style={{ fontSize: 16, lineHeight: 1 }}>×</span>
              </IconButton>
            ) : null,
          }}
        />
        <TextField
          select
          size="small"
          label="Durum"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          sx={{ minWidth: 150, bgcolor: "#fff", borderRadius: 2 }}
        >
          <MenuItem value="">Tümü</MenuItem>
          <MenuItem value="open">Açık</MenuItem>
          <MenuItem value="in_progress">Üretimde</MenuItem>
          <MenuItem value="completed">Tamamlandı</MenuItem>
        </TextField>
        <Button
          variant="contained"
          color="error"
          disabled={!selectedIds.length || deleting}
          onClick={handleDeleteSelected}
          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800, whiteSpace: "nowrap" }}
        >
          {deleting ? "Siliniyor..." : "İş Emri Sil"}
        </Button>
      </Stack>

      {!list.length && total === 0 ? (
        <EmptyState description="Henüz iş emri yok." />
      ) : (
        <Paper elevation={0} sx={{ p: { xs: 1, md: 1.5 }, borderRadius: 4, bgcolor: "#f5f3ff", border: "1px solid #c7d2fe", boxShadow: "0 12px 30px rgba(79, 70, 229, 0.10)", overflow: "hidden" }}>
          <Table size="small" sx={{ bgcolor: "#fff", borderRadius: 3, overflow: "hidden", "& .MuiTableCell-head": { bgcolor: "#ede9fe", color: "#3730a3", fontWeight: 800, borderColor: "#ddd6fe", whiteSpace: "nowrap" }, "& .MuiTableCell-root": { borderColor: "#e5e7eb", py: 1.25 }, "& .MuiTableBody-root .MuiTableRow-root:hover": { bgcolor: "#fafaff" } }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Müşteri</TableCell>
                <TableCell>Sipariş No</TableCell>
                <TableCell>İş Emri No</TableCell>
                <TableCell>Müşteri Ürün Kodu</TableCell>
                <TableCell>Miktar (Paket)</TableCell>
                <TableCell>Termin</TableCell>
                <TableCell>Durum</TableCell>
                <TableCell align="center">İşlem</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((o) => (
                <TableRow key={o.id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIds.includes(Number(o.id))}
                      onChange={() => toggleSelected(o.id)}
                      disabled={deleting}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.75} alignItems="baseline" useFlexGap flexWrap="wrap">
                      <Typography component="span" variant="body2">{o.customer_name || "—"}</Typography>
                      {o.customer_code ? (
                        <Typography component="span" variant="caption" color="text.secondary">
                          ({o.customer_code})
                        </Typography>
                      ) : null}
                    </Stack>
                  </TableCell>
                  <TableCell>{o.sales_order_no || "—"}</TableCell>
                  <TableCell>{orderNoCell(o)}</TableCell>
                  <TableCell>{o.customer_product_code || "—"}</TableCell>
                  <TableCell>{formatInteger(o.package_qty)}</TableCell>
                  <TableCell>{formatDateOnly(o.due_date)}</TableCell>
                  <TableCell>
                    <StatusBadge value={o.status || "—"} useWorkOrderLabels />
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.75} justifyContent="center" flexWrap="wrap" useFlexGap>
                      <Button size="small" variant="contained" color="primary" disabled={!canAssign} title={!canAssign ? "Rezervasyon ataması sadece üretim müdürüne özeldir." : undefined} sx={{ color: "#111827", bgcolor: "#93c5fd", borderRadius: 2, textTransform: "none", fontWeight: 800, boxShadow: "none", "&:hover": { bgcolor: "#60a5fa", boxShadow: "none" } }} onClick={() => setReservationWorkOrder(o)}>
                        Rezervasyon
                      </Button>
                      <Button size="small" variant="contained" color="warning" disabled={!canAssign} title={!canAssign ? "Bıçak ataması sadece üretim müdürüne özeldir." : undefined} sx={{ color: "#111827", bgcolor: "#fde68a", borderRadius: 2, textTransform: "none", fontWeight: 800, boxShadow: "none", "&:hover": { bgcolor: "#fcd34d", boxShadow: "none" } }} onClick={() => setKnifeWorkOrder(o)}>
                        Bıçak
                      </Button>
                      <Button size="small" variant="contained" color="success" sx={{ color: "#111827", bgcolor: "#86efac", borderRadius: 2, textTransform: "none", fontWeight: 800, boxShadow: "none", "&:hover": { bgcolor: "#4ade80", boxShadow: "none" } }} onClick={() => setMaterialsWorkOrder(o)}>
                        Malzemeler
                      </Button>
                      <Button size="small" variant="contained" color="info" sx={{ color: "#111827", bgcolor: "#bae6fd", borderRadius: 2, textTransform: "none", fontWeight: 800, boxShadow: "none", "&:hover": { bgcolor: "#7dd3fc", boxShadow: "none" } }} onClick={() => setShipmentWorkOrder(o)}>
                        Sevk
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            {total > ITEMS_PER_PAGE ? (
              <TableFooter>
                <TableRow>
                  <TablePagination
                    count={total}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    rowsPerPage={ITEMS_PER_PAGE}
                    rowsPerPageOptions={[ITEMS_PER_PAGE]}
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
                    colSpan={9}
                    sx={{ borderBottom: "none" }}
                  />
                </TableRow>
              </TableFooter>
            ) : null}
          </Table>
        </Paper>
      )}

      {reservationWorkOrder ? (
        <WorkOrderPaperReservationDialog
          workOrder={reservationWorkOrder}
          onClose={() => setReservationWorkOrder(null)}
          onSuccess={() => setReservationWorkOrder(null)}
        />
      ) : null}

      {knifeWorkOrder ? (
        <WorkOrderKnifeDialog
          workOrder={knifeWorkOrder}
          onClose={() => setKnifeWorkOrder(null)}
          onSuccess={() => setKnifeWorkOrder(null)}
        />
      ) : null}

      {materialsWorkOrder ? (
        <WorkOrderMaterialsDialog
          workOrder={materialsWorkOrder}
          onClose={() => setMaterialsWorkOrder(null)}
        />
      ) : null}

      {shipmentWorkOrder ? (
        <WorkOrderShipmentDialog
          workOrder={shipmentWorkOrder}
          onClose={() => setShipmentWorkOrder(null)}
          onSuccess={(result) => {
            alert(`${result.koli} koli stoktan düşüldü.`);
          }}
        />
      ) : null}
    </Stack>
  );
}
